import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Send, FileEdit, Ban, Trash2, RefreshCw, MailPlus, ArrowLeft, Mail, X, Paperclip, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "mt-1.5 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const FOLDER_ICONS = { INBOX: Inbox, Sent: Send, Drafts: FileEdit, Junk: Ban, Trash: Trash2 };
const FOLDER_LABELS = { INBOX: "受信トレイ", Sent: "送信済み", Drafts: "下書き", Junk: "迷惑メール", Trash: "ゴミ箱" };

const fmtDate = (raw) => {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("ja-JP", { month: "numeric", day: "numeric" });
};

const addrOf = (header) => {
  const m = /<([^>]+)>/.exec(header || "");
  return m ? m[1] : header || "";
};

export default function AdminMail() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);

  const accountParam = useCallback(() => (account ? { account } : {}), [account]);

  const loadFolders = useCallback(() => {
    api.get("/admin/mail/folders", { params: accountParam() }).then((r) => setFolders(r.data)).catch(() => {});
  }, [accountParam]);

  const loadMessages = useCallback((f) => {
    setLoading(true);
    api
      .get("/admin/mail/messages", { params: { folder: f, limit: 100, ...accountParam() } })
      .then((r) => setMessages(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [accountParam]);

  useEffect(() => {
    api.get("/admin/mail/accounts").then((r) => {
      setAccounts(r.data);
      if (r.data.length) setAccount(r.data[0].address);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    setDetail(null);
    loadMessages(folder);
  }, [folder, loadMessages]);

  const openMessage = async (msg) => {
    setDetailLoading(true);
    setDetail({ ...msg, text: "" });
    try {
      const r = await api.get(`/admin/mail/messages/${msg.uid}`, { params: { folder, ...accountParam() } });
      setDetail(r.data);
      setMessages((prev) => prev.map((x) => (x.uid === msg.uid ? { ...x, seen: true } : x)));
      loadFolders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refresh = () => {
    loadFolders();
    loadMessages(folder);
  };

  const openReply = () => {
    if (!detail) return;
    const quoted = (detail.text || "").split("\n").map((l) => `> ${l}`).join("\n");
    setCompose({
      to: addrOf(detail.from),
      subject: detail.subject.startsWith("Re:") ? detail.subject : `Re: ${detail.subject}`,
      body: `\n\n―― 元のメッセージ ――\n${quoted}`,
      in_reply_to: detail.message_id || null,
    });
  };

  const sendMail = async () => {
    setSending(true);
    try {
      await api.post("/admin/mail/send", { ...compose, ...accountParam() });
      toast.success("メールを送信しました");
      setCompose(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async () => {
    if (!detail) return;
    const inTrash = folder.toLowerCase() === "trash";
    if (!window.confirm(inTrash ? "完全に削除しますか？元に戻せません。" : "ゴミ箱に移動しますか？")) return;
    try {
      await api.delete(`/admin/mail/messages/${detail.uid}`, { params: { folder, ...accountParam() } });
      toast.success(inTrash ? "メールを完全に削除しました" : "ゴミ箱に移動しました");
      setDetail(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const trainMessage = async (isSpam) => {
    if (!detail) return;
    try {
      await api.post(`/admin/mail/messages/${detail.uid}/train`, { folder, is_spam: isSpam, ...accountParam() });
      toast.success(
        isSpam
          ? "迷惑メールとして学習し、迷惑メールフォルダに移動しました"
          : "迷惑メールではないと学習し、受信トレイに戻しました"
      );
      setDetail(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const folderList = folders.length ? folders : [{ name: "INBOX", total: 0, unseen: 0 }];

  return (
    <div data-testid="admin-mail-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">メールボックス</h1>
          <div className="mt-1 flex items-center gap-2">
            {accounts.length > 1 ? (
              <select
                data-testid="mail-account-select"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                className="border border-line bg-ink px-2 py-1 font-mono text-[10px] tracking-[0.15em] text-accent outline-none focus:border-accent"
              >
                {accounts.map((a) => (
                  <option key={a.address} value={a.address}>
                    {a.address}
                  </option>
                ))}
              </select>
            ) : (
              <p className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{account || "MAILBOX"}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="mail-refresh-button"
            onClick={refresh}
            className="flex items-center gap-2 border border-line px-4 py-2.5 text-sm text-gray-400 transition-colors hover:border-accent hover:text-accent"
          >
            <RefreshCw className="h-4 w-4" />
            更新
          </button>
          <button
            data-testid="mail-compose-button"
            onClick={() => setCompose({ to: "", subject: "", body: "", in_reply_to: null })}
            className="flex items-center gap-2 bg-accent px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-accent/85"
          >
            <MailPlus className="h-4 w-4" />
            新規メール
          </button>
        </div>
      </div>

      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-line">
        {folderList.map((f) => {
          const Icon = FOLDER_ICONS[f.name] || Inbox;
          const active = folder === f.name;
          return (
            <button
              key={f.name}
              data-testid={`mail-folder-${f.name}`}
              onClick={() => setFolder(f.name)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition-colors ${
                active ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-white"
              }`}
            >
              <Icon className="h-4 w-4" />
              {FOLDER_LABELS[f.name] || f.name}
              {f.unseen > 0 && (
                <span data-testid={`mail-unseen-${f.name}`} className="bg-accent px-1.5 font-mono text-[10px] font-bold text-ink">
                  {f.unseen}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(320px,420px)_1fr]">
        {/* message list */}
        <div data-testid="mail-message-list" className={`border border-line bg-panel ${detail ? "hidden lg:block" : ""}`}>
          {loading ? (
            <p className="p-6 font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
          ) : messages.length === 0 ? (
            <p data-testid="mail-empty" className="p-6 text-sm text-gray-500">
              メッセージはありません。
            </p>
          ) : (
            messages.map((msg) => (
              <button
                key={msg.uid}
                data-testid={`mail-message-item-${msg.uid}`}
                onClick={() => openMessage(msg)}
                className={`block w-full border-b border-line px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-coal ${
                  detail?.uid === msg.uid ? "bg-coal" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className={`flex min-w-0 items-center gap-2 truncate text-sm ${msg.seen ? "text-gray-400" : "font-bold text-white"}`}>
                    {!msg.seen && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
                    {addrOf(msg.from) || msg.from}
                  </p>
                  <span className="shrink-0 font-mono text-[10px] text-gray-500">{fmtDate(msg.date)}</span>
                </div>
                <p className={`mt-1 truncate text-xs ${msg.seen ? "text-gray-500" : "text-gray-300"}`}>{msg.subject}</p>
              </button>
            ))
          )}
        </div>

        {/* detail pane */}
        <div data-testid="mail-detail-pane" className={`border border-line bg-panel ${detail ? "" : "hidden lg:block"}`}>
          {!detail ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-3 p-10 text-gray-600">
              <Mail className="h-8 w-8" />
              <p className="text-sm">メッセージを選択してください</p>
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="border-b border-line p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <button data-testid="mail-back-button" onClick={() => setDetail(null)} className="mb-3 flex items-center gap-1.5 text-xs text-gray-500 hover:text-white lg:hidden">
                      <ArrowLeft className="h-3.5 w-3.5" />
                      一覧に戻る
                    </button>
                    <h2 data-testid="mail-detail-subject" className="font-mincho text-lg font-bold text-white">
                      {detail.subject}
                    </h2>
                    <dl className="mt-3 space-y-1 text-xs text-gray-400">
                      <div className="flex gap-2">
                        <dt className="w-12 shrink-0 font-mono text-[10px] leading-5 tracking-[0.15em] text-gray-600">FROM</dt>
                        <dd className="break-all">{detail.from}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="w-12 shrink-0 font-mono text-[10px] leading-5 tracking-[0.15em] text-gray-600">TO</dt>
                        <dd className="break-all">{detail.to}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="w-12 shrink-0 font-mono text-[10px] leading-5 tracking-[0.15em] text-gray-600">DATE</dt>
                        <dd>{detail.date ? new Date(detail.date).toLocaleString("ja-JP") : ""}</dd>
                      </div>
                    </dl>
                    {detail.attachments?.length > 0 && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                        <Paperclip className="h-3.5 w-3.5" />
                        {detail.attachments.join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    {folder === "Junk" && (
                      <button
                        data-testid="mail-not-spam-button"
                        onClick={() => trainMessage(false)}
                        className="flex items-center gap-2 border border-green-500/50 px-4 py-2 text-sm text-green-400 transition-colors hover:bg-green-500 hover:text-white"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        迷惑メールではない
                      </button>
                    )}
                    {folder === "INBOX" && (
                      <button
                        data-testid="mail-report-spam-button"
                        onClick={() => trainMessage(true)}
                        className="flex items-center gap-2 border border-yellow-500/50 px-4 py-2 text-sm text-yellow-400 transition-colors hover:bg-yellow-500 hover:text-ink"
                      >
                        <Ban className="h-4 w-4" />
                        迷惑メールにする
                      </button>
                    )}
                    <button
                      data-testid="mail-delete-button"
                      onClick={deleteMessage}
                      className="flex items-center gap-2 border border-red-500/50 px-4 py-2 text-sm text-red-400 transition-colors hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                      削除
                    </button>
                    <button
                      data-testid="mail-reply-button"
                      onClick={openReply}
                      disabled={detailLoading}
                      className="flex items-center gap-2 border border-accent px-4 py-2 text-sm text-accent transition-colors hover:bg-accent hover:text-ink disabled:opacity-40"
                    >
                      <Send className="h-4 w-4" />
                      返信
                    </button>
                  </div>
                </div>
              </div>
              {detailLoading ? (
                <p className="p-6 font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
              ) : (
                <div data-testid="mail-detail-body" className="whitespace-pre-wrap break-words p-6 text-sm leading-7 text-gray-300">
                  {detail.text}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* compose modal */}
      {compose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setCompose(null)}>
          <div
            data-testid="mail-compose-modal"
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto border border-line bg-coal p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{compose.in_reply_to ? "メールに返信" : "新規メール"}</h2>
              <button type="button" data-testid="mail-compose-close" onClick={() => setCompose(null)} className="text-gray-500 transition-colors hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.25em] text-gray-500">宛先</span>
                <input
                  type="email"
                  data-testid="mail-to-input"
                  value={compose.to}
                  onChange={(e) => setCompose((f) => ({ ...f, to: e.target.value }))}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.25em] text-gray-500">件名</span>
                <input
                  type="text"
                  data-testid="mail-subject-input"
                  value={compose.subject}
                  onChange={(e) => setCompose((f) => ({ ...f, subject: e.target.value }))}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-[0.25em] text-gray-500">本文</span>
                <textarea
                  rows={10}
                  data-testid="mail-body-input"
                  value={compose.body}
                  onChange={(e) => setCompose((f) => ({ ...f, body: e.target.value }))}
                  className={`${inputCls} resize-y leading-7`}
                />
              </label>
              <p className="text-xs text-gray-600">署名（会社名・住所・サイトURL）は自動で添付されます。</p>
              <button
                data-testid="mail-send-button"
                onClick={sendMail}
                disabled={sending || !compose.to || !compose.subject || !compose.body}
                className="flex w-full items-center justify-center gap-2 bg-accent py-3.5 text-sm font-bold text-ink transition-colors hover:bg-accent/85 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
                {sending ? "送信中..." : "メールを送信する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
